import { addMonths, isBefore, isValid, parseISO } from 'date-fns';

// Works out which home claims UW removed, so the Incidents tab's not-rated rows (#36608) don't bring
// them back once they are past 3 years. A removal leaves no marker: scrubIncidents deletes the claim
// from homeClaimDates on every segment of the term it was removed in, and renewals carry the
// remaining homeClaimDates forward unchanged. So a claim that is in one term (or the bound offer, for
// term 1) and missing from a later term was removed, provided it was still inside the 36-month
// rating window at the later term's effective date. A claim that goes missing after that aged out.
const RATED_CLAIM_LOOKBACK_MONTHS = 36;

export const EMPTY_REMOVED_HOME_CLAIMS = Object.freeze({ keys: new Set(), dates: new Set() });

// homeClaimDates is AWSJSON on PolicyDetails and QuoteDetails, so it arrives serialized
export const parseAwsJson = (value, fallback) => {
  if (!value) {
    return fallback;
  }
  try {
    return typeof value === 'string' ? JSON.parse(value) : value;
  } catch {
    return fallback;
  }
};

// date|class keys in a homeClaimDates map, or null when it isn't there at all (no information)
const getClaimKeys = (homeClaimDates) => {
  const claimDates = parseAwsJson(homeClaimDates, null);
  if (!claimDates || typeof claimDates !== 'object') {
    return null;
  }
  return Object.entries(claimDates).flatMap(([date, claimList]) =>
    (Array.isArray(claimList) ? claimList : []).map((claim) => `${date}|${claim?.class}`)
  );
};

const toTermSnapshot = (term) => {
  const segmentKeys = (term.segments || []).map((segment) => getClaimKeys(segment?.homeClaimDates)).filter(Boolean);
  return segmentKeys.length ? { referenceDate: term.effectiveDate, keys: new Set(segmentKeys.flat()) } : null;
};

/**
 * Chronological homeClaimDates snapshots for a lineage: the earliest term's bound offer, then each
 * term (the union of its segments, since a removal reaches every segment of its term).
 * `terms` are { term, effectiveDate, segments: [{ homeClaimDates }], offer: { quote: { homeClaimDates } } }.
 */
export const buildHomeClaimSnapshots = (terms) => {
  const ordered = [...(terms || [])].sort((a, b) => (a.term ?? 0) - (b.term ?? 0));
  if (!ordered.length) {
    return [];
  }

  const offerKeys = getClaimKeys(ordered[0].offer?.quote?.homeClaimDates);
  const offerSnapshot = offerKeys && { referenceDate: ordered[0].effectiveDate, keys: new Set(offerKeys) };
  return [offerSnapshot, ...ordered.map(toTermSnapshot)].filter(Boolean);
};

const wasStillRated = (claimDate, referenceDate) => {
  const lossDate = parseISO(claimDate);
  const asOf = parseISO(referenceDate || '');
  return isValid(lossDate) && isValid(asOf) && isBefore(asOf, addMonths(lossDate, RATED_CLAIM_LOOKBACK_MONTHS));
};

/** { keys: Set of `date|class`, dates: Set of dates } for every claim UW removed. */
export const getRemovedHomeClaims = (snapshots) => {
  const keys = new Set();
  const seen = new Set();
  for (const snapshot of snapshots || []) {
    for (const key of seen) {
      const [claimDate] = key.split('|');
      if (!snapshot.keys.has(key) && wasStillRated(claimDate, snapshot.referenceDate)) {
        keys.add(key);
      }
    }
    for (const key of snapshot.keys) {
      seen.add(key);
    }
  }
  return { keys, dates: new Set([...keys].map((key) => key.split('|')[0])) };
};

// Same lineage the way price-details finds the previous term: account + line prefix (so `home` and
// `home2` stay apart; the carrier suffix can change between terms) and the same policy type
const getLineageStem = (policyId) => (policyId || '').split('-').slice(0, 2).join('-');

/** Ids of the earlier terms of this policy's lineage, oldest first. */
export const getLineagePriorTermIds = (policy, policiesList) => {
  if (!policy?.id || !(policy.term > 1)) {
    return [];
  }
  const stem = getLineageStem(policy.id);
  return (policiesList || [])
    .filter(
      (listed) =>
        listed.id !== policy.id &&
        getLineageStem(listed.id) === stem &&
        listed.policyType === policy.policyType &&
        listed.term < policy.term
    )
    .sort((a, b) => a.term - b.term)
    .map((listed) => listed.id);
};
